import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, LogOut, Save, RotateCcw, Plus, Trash2, ExternalLink, RefreshCw, Inbox, Upload, KeyRound, Archive } from 'lucide-react';
import * as Content from '@/data/content';
import { applySection, DEFAULTS } from '@/lib/contentLoader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'asa_admin_token';
const deep = (v) => JSON.parse(JSON.stringify(v));
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const inputCls = 'w-full bg-black/60 border border-white/15 rounded-sm px-3 py-2.5 text-white text-sm focus:border-brand-gold focus:outline-none transition-colors duration-300';
const labelCls = 'block text-[11px] uppercase tracking-[0.18em] text-gray-500 font-semibold mb-1.5';

const TABS = [
  { key: 'CONTACT', label: 'Contact & Location', type: 'object', fields: [
    { key: 'location', label: 'Location / Field' },
    { key: 'mapsEmbed', label: 'Google Maps Embed URL' },
    { key: 'mapsLink', label: 'Directions URL' },
    { key: 'phoneDisplay', label: 'Phone (display)' },
    { key: 'whatsapp', label: 'WhatsApp Link' },
    { key: 'email', label: 'Email' },
    { key: 'instagram', label: 'Instagram URL' },
    { key: 'instagramHandle', label: 'Instagram Handle' },
  ]},
  { key: 'HERO', label: 'Hero', type: 'object', fields: [
    { key: 'headline', label: 'Headline (one line per row)', type: 'lines' },
    { key: 'subheadline', label: 'Subheadline', type: 'textarea' },
    { key: 'supporting', label: 'Supporting line' },
    { key: 'trustLine', label: 'Trust line items (one per row)', type: 'lines' },
  ]},
  { key: 'PACKAGES', label: 'Packages & Prices', type: 'array', itemLabel: 'name', fields: [
    { key: 'id', label: 'ID (no spaces)' },
    { key: 'name', label: 'Name' },
    { key: 'price', label: 'Price ($)' },
    { key: 'per', label: 'Per (Month / Session)' },
    { key: 'cadence', label: 'Cadence' },
    { key: 'blurb', label: 'Short description', type: 'textarea' },
    { key: 'cta', label: 'Button text' },
    { key: 'badge', label: 'Badge (e.g. Most Popular — empty for none)' },
    { key: 'elite', label: 'Premium gold style', type: 'check' },
    { key: 'features', label: 'Features (one per row)', type: 'lines' },
  ]},
  { key: 'PROGRAMS', label: 'Additional Programs', type: 'array', itemLabel: 'title', fields: [
    { key: 'icon', label: 'Icon name (lucide)' },
    { key: 'title', label: 'Title' },
    { key: 'blurb', label: 'Description', type: 'textarea' },
    { key: 'cta', label: 'Button text' },
    { key: 'program', label: 'Program value (form)' },
    { key: 'items', label: 'Focus items (one per row)', type: 'lines' },
  ]},
  { key: 'AGE_GROUPS', label: 'Age Groups', type: 'array', itemLabel: 'group', fields: [
    { key: 'group', label: 'Group (e.g. U8)' },
    { key: 'ages', label: 'Ages' },
    { key: 'focus', label: 'Development focus', type: 'textarea' },
  ]},
  { key: 'COACHES', label: 'Coaches', type: 'array', itemLabel: 'name', fields: [
    { key: 'role', label: 'Role' },
    { key: 'name', label: 'Name' },
    { key: 'credentials', label: 'Credentials' },
    { key: 'bio', label: 'Bio', type: 'textarea' },
    { key: 'image', label: 'Photo', type: 'image' },
    { key: 'imagePos', label: 'Image crop position (e.g. center 14%)' },
    { key: 'monogram', label: 'Monogram (2 letters)' },
    { key: 'resumeUrl', label: 'Official resume link (Google Drive / PDF URL)' },
  ]},
  { key: 'GALLERY', label: 'Gallery', type: 'array', itemLabel: 'alt', fields: [
    { key: 'src', label: 'Image', type: 'image' },
    { key: 'category', label: 'Category (Training/Matches/Players/Coaches/Camps/Community/Events)' },
    { key: 'alt', label: 'Caption' },
  ]},
  { key: 'TESTIMONIALS', label: 'Testimonials', type: 'array', itemLabel: 'label', fields: [
    { key: 'quote', label: 'Quote', type: 'textarea' },
    { key: 'label', label: 'Name / label' },
    { key: 'placeholder', label: 'Mark as placeholder', type: 'check' },
  ]},
  { key: 'EVENTS', label: 'Events', type: 'array', itemLabel: 'name', fields: [
    { key: 'name', label: 'Event name' },
    { key: 'type', label: 'Type (Camp/Clinic/Tryout/Community)' },
    { key: 'date', label: 'Date' },
    { key: 'time', label: 'Time' },
    { key: 'location', label: 'Location' },
    { key: 'ages', label: 'Age group' },
    { key: 'price', label: 'Price' },
    { key: 'description', label: 'Description', type: 'textarea' },
  ]},
  { key: 'STATS', label: 'Stats Band', type: 'array', itemLabel: 'label', fields: [
    { key: 'value', label: 'Value' },
    { key: 'label', label: 'Label' },
  ]},
  { key: 'WHY_FEATURES', label: 'Why ASA Cards', type: 'array', itemLabel: 'title', fields: [
    { key: 'icon', label: 'Icon name (lucide)' },
    { key: 'title', label: 'Title' },
    { key: 'text', label: 'Text', type: 'textarea' },
  ]},
  { key: 'PARENT_PROMISE', label: 'Parent Promise', type: 'array', itemLabel: 'title', fields: [
    { key: 'icon', label: 'Icon name (lucide)' },
    { key: 'title', label: 'Title' },
  ]},
  { key: 'PATHWAY', label: 'Player Pathway', type: 'array', itemLabel: 'stage', fields: [
    { key: 'stage', label: 'Stage' },
    { key: 'ages', label: 'Ages' },
    { key: 'text', label: 'Text', type: 'textarea' },
  ]},
  { key: 'PROGRESS_SKILLS', label: 'Progress Skills', type: 'array', itemLabel: 'skill', fields: [
    { key: 'skill', label: 'Skill' },
    { key: 'level', label: 'Level (0-100)' },
  ]},
  { key: 'TRYOUT', label: 'Tryout Page', type: 'object', fields: [
    { key: 'headline', label: 'Headline' },
    { key: 'text', label: 'Intro text', type: 'textarea' },
    { key: 'bring', label: 'What to bring (one per row)', type: 'lines' },
    { key: 'expectations', label: 'Expectations (one per row)', type: 'lines' },
    { key: 'dates', label: 'Available dates (one per row)', type: 'lines' },
  ]},
  { key: 'PROGRAM_OPTIONS', label: 'Form Program Options', type: 'strings' },
  { key: 'PHILOSOPHY_VALUES', label: 'Philosophy Values', type: 'strings' },
  { key: 'CHARACTER_VALUES', label: 'Character Values', type: 'strings' },
  { key: 'SMALL_GROUP_BENEFITS', label: 'Small Group Benefits', type: 'strings' },
  { key: 'PATHWAY_DESTINATIONS', label: 'Pathway Destinations', type: 'strings' },
  { key: 'MARQUEE_ITEMS', label: 'Marquee Words', type: 'strings' },
  { key: 'ASSETS', label: 'Images / Assets', type: 'object', fields: [
    { key: 'logo', label: 'Logo', type: 'image' },
    { key: 'squadBanner', label: 'Hero squad banner', type: 'image' },
    { key: 'coachSuleiman', label: 'Coach Suleiman photo', type: 'image' },
  ]},
  { key: 'GIRLS_SPOTS', label: 'Girls Winter Spots', type: 'spots' },
  { key: 'PASSWORD', label: 'Password — كلمة المرور', type: 'password' },
  { key: 'REGISTRATIONS', label: 'Registrations', type: 'registrations' },
];

function ImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, { headers: authHeader() });
      onChange(data.url);
      toast.success('Image uploaded — تم رفع الصورة');
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Upload failed');
    } finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-3" data-testid="image-field">
      {value
        ? <img src={value} alt="preview" className="w-14 h-14 rounded-sm object-cover border border-white/15 shrink-0" />
        : <div className="w-14 h-14 rounded-sm border border-dashed border-white/20 shrink-0" />}
      <input className={inputCls} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder="/assets/... or upload →" />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="image-upload-button"
        className="shrink-0 flex items-center gap-2 border border-brand-gold/60 text-brand-gold font-display tracking-widest text-sm px-4 py-2.5 rounded-sm hover:bg-brand-gold hover:text-night disabled:opacity-60 transition-colors duration-300">
        <Upload size={15} /> {uploading ? 'جاري الرفع…' : 'رفع صورة'}
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="image-upload-input" />
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  if (field.type === 'image') return <ImageField value={value} onChange={onChange} />;
  if (field.type === 'textarea') {
    return <textarea className={inputCls} rows={3} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
  }
  if (field.type === 'lines') {
    return <textarea className={inputCls} rows={Math.max(3, (value || []).length + 1)} value={(value || []).join('\n')}
      onChange={e => onChange(e.target.value.split('\n').filter(s => s.trim() !== ''))} placeholder="One per line" />;
  }
  if (field.type === 'check') {
    return (
      <label className="flex items-center gap-2 text-sm text-gray-300 py-2 cursor-pointer">
        <input type="checkbox" className="h-4 w-4 accent-[#C5A059]" checked={!!value} onChange={e => onChange(e.target.checked)} />
        {field.label}
      </label>
    );
  }
  return <input className={inputCls} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
}

const fullWidth = (f) => ['textarea', 'lines', 'image'].includes(f.type) ? 'sm:col-span-2' : '';

function SectionActions({ saving, onSave, onReset }) {
  return (
    <div className="flex flex-wrap gap-3 mt-6">
      <button onClick={onSave} disabled={saving} data-testid="admin-save-button"
        className="flex items-center gap-2 bg-brand-gold text-night font-display tracking-widest text-lg px-8 py-3 rounded-sm hover:brightness-110 disabled:opacity-60 transition-[filter] duration-300">
        <Save size={17} /> {saving ? 'SAVING…' : 'SAVE CHANGES — حفظ'}
      </button>
      <button onClick={onReset} disabled={saving} data-testid="admin-reset-button"
        className="flex items-center gap-2 border border-white/20 text-gray-300 font-display tracking-widest text-lg px-8 py-3 rounded-sm hover:border-brand-red hover:text-brand-red transition-colors duration-300">
        <RotateCcw size={16} /> RESET TO DEFAULT
      </button>
    </div>
  );
}

function useSection(key) {
  const [saving, setSaving] = useState(false);
  const save = async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/${key}`, { data }, { headers: authHeader() });
      applySection(key, deep(data));
      toast.success('Saved — live on the website now');
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
    } finally { setSaving(false); }
  };
  const reset = async (apply) => {
    setSaving(true);
    try {
      await axios.delete(`${API}/content/${key}`, { headers: authHeader() });
      const def = deep(DEFAULTS[key]);
      applySection(key, def);
      apply(def);
      toast.success('Restored to defaults');
    } catch (e) { toast.error('Reset failed'); }
    finally { setSaving(false); }
  };
  return { saving, save, reset };
}

function ObjectEditor({ tab }) {
  const [data, setData] = useState(() => deep(Content[tab.key]));
  const { saving, save, reset } = useSection(tab.key);
  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-5">
        {tab.fields.map(f => (
          <div key={f.key} className={fullWidth(f)}>
            {f.type !== 'check' && <label className={labelCls}>{f.label}</label>}
            <FieldInput field={f} value={data[f.key]} onChange={v => setData(d => ({ ...d, [f.key]: v }))} />
          </div>
        ))}
      </div>
      <SectionActions saving={saving} onSave={() => save(data)} onReset={() => reset(setData)} />
    </div>
  );
}

function ArrayEditor({ tab }) {
  const [items, setItems] = useState(() => deep(Content[tab.key]));
  const { saving, save, reset } = useSection(tab.key);
  const setField = (i, k, v) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => {
    const blank = {};
    tab.fields.forEach(f => { blank[f.key] = f.type === 'lines' ? [] : f.type === 'check' ? false : ''; });
    setItems(arr => [...arr, blank]);
  };
  return (
    <div className="space-y-6">
      {items.map((item, i) => (
        <div key={i} className="glass-card rounded-sm p-5 relative">
          <div className="flex items-center justify-between mb-4">
            <span className="font-display text-lg tracking-widest text-brand-gold uppercase">{item[tab.itemLabel] || `Item ${i + 1}`}</span>
            <button onClick={() => setItems(arr => arr.filter((_, idx) => idx !== i))} data-testid={`admin-remove-item-${i}`}
              className="text-gray-500 hover:text-brand-red transition-colors" aria-label="Remove item">
              <Trash2 size={17} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {tab.fields.map(f => (
              <div key={f.key} className={fullWidth(f)}>
                {f.type !== 'check' && <label className={labelCls}>{f.label}</label>}
                <FieldInput field={f} value={item[f.key]} onChange={v => setField(i, f.key, v)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={addItem} data-testid="admin-add-item-button"
        className="flex items-center gap-2 border border-dashed border-brand-gold/50 text-brand-gold font-display tracking-widest text-lg px-6 py-3 rounded-sm hover:bg-brand-gold/10 transition-colors duration-300">
        <Plus size={17} /> ADD NEW
      </button>
      <SectionActions saving={saving} onSave={() => save(items)} onReset={() => reset(setItems)} />
    </div>
  );
}

function StringsEditor({ tab }) {
  const [items, setItems] = useState(() => deep(Content[tab.key]));
  const { saving, save, reset } = useSection(tab.key);
  return (
    <div>
      <label className={labelCls}>One item per line</label>
      <textarea className={inputCls} rows={Math.max(5, items.length + 1)} value={items.join('\n')}
        onChange={e => setItems(e.target.value.split('\n').filter(s => s.trim() !== ''))} />
      <SectionActions saving={saving} onSave={() => save(items)} onReset={() => reset(setItems)} />
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { currentPassword: current, newPassword: next }, { headers: authHeader() });
      toast.success('Password updated — تم تغيير كلمة المرور');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to change password');
    } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="max-w-md space-y-5" data-testid="change-password-form">
      <p className="text-sm text-gray-400 leading-relaxed">
        Change the admin password. The new password works immediately and stays active even after restarts — the original password stops working.
        غيّر كلمة المرور — تعمل الجديدة فوراً وتبقى محفوظة حتى بعد إعادة التشغيل.
      </p>
      <div>
        <label className={labelCls}>Current Password — كلمة المرور الحالية</label>
        <input type="password" className={inputCls} value={current} onChange={e => setCurrent(e.target.value)} required data-testid="current-password" autoComplete="current-password" />
      </div>
      <div>
        <label className={labelCls}>New Password — كلمة المرور الجديدة</label>
        <input type="password" className={inputCls} value={next} onChange={e => setNext(e.target.value)} required minLength={8} data-testid="new-password" autoComplete="new-password" />
      </div>
      <div>
        <label className={labelCls}>Confirm New Password — تأكيد كلمة المرور</label>
        <input type="password" className={inputCls} value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} data-testid="confirm-password" autoComplete="new-password" />
      </div>
      <button type="submit" disabled={saving} data-testid="change-password-button"
        className="flex items-center gap-2 bg-brand-gold text-night font-display tracking-widest text-lg px-8 py-3 rounded-sm hover:brightness-110 disabled:opacity-60 transition-[filter] duration-300">
        <KeyRound size={17} /> {saving ? 'UPDATING…' : 'UPDATE PASSWORD — تحديث'}
      </button>
    </form>
  );
}

function SpotsEditor() {
  const [info, setInfo] = useState(null);
  const [remaining, setRemaining] = useState('');
  const [total, setTotal] = useState('');
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/girls-spots`);
      setInfo(data);
      setRemaining(String(data.remaining));
      setTotal(String(data.total));
    } catch { toast.error('Could not load spots'); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    const rem = parseInt(remaining, 10);
    const tot = parseInt(total, 10);
    if (isNaN(rem) || rem < 0 || isNaN(tot) || tot < 1 || rem > tot) { toast.error('Check the numbers — remaining cannot exceed total'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/girls-spots`, { remaining: rem, total: tot }, { headers: authHeader() });
      toast.success('Spots updated — live on the site now');
      load();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  if (!info) return <p className="text-gray-500">Loading…</p>;
  const taken = Math.max(0, info.total - info.remaining);
  return (
    <div data-testid="spots-editor">
      <p className="text-sm text-gray-400 leading-relaxed mb-6">
        This number decreases automatically by 1 with every Girls Winter registration. You can change the remaining count and the total capacity (for a new season) at any time —
        ينقص تلقائياً مع كل تسجيل، ويمكنك تعديل المتبقي والعدد الإجمالي يدوياً من هنا.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="glass-card rounded-sm p-5 text-center">
          <div className="font-display text-4xl text-white">{info.total}</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mt-1">Total Spots</div>
        </div>
        <div className="glass-card rounded-sm p-5 text-center">
          <div className="font-display text-4xl text-brand-gold">{taken}</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mt-1">Registered</div>
        </div>
        <div className="glass-card rounded-sm p-5 text-center gold-hairline">
          <div className="font-display text-4xl text-brand-red">{info.remaining}</div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mt-1">Remaining</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 max-w-md">
        <div>
          <label className={labelCls}>Total capacity — العدد الإجمالي</label>
          <input type="number" min="1" max="500" className={inputCls} value={total} onChange={e => setTotal(e.target.value)} data-testid="spots-total-input" />
        </div>
        <div>
          <label className={labelCls}>Remaining now — المتبقي الآن</label>
          <input type="number" min="0" max="500" className={inputCls} value={remaining} onChange={e => setRemaining(e.target.value)} data-testid="spots-input" />
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="spots-save-button"
        className="mt-5 flex items-center gap-2 bg-brand-gold text-night font-display tracking-widest text-lg px-8 py-3 rounded-sm hover:brightness-110 disabled:opacity-60 transition-[filter] duration-300">
        <Save size={17} /> {saving ? 'SAVING…' : 'SAVE — حفظ'}
      </button>
    </div>
  );
}

function RegistrationsViewer() {
  const [regs, setRegs] = useState(null);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/registrations`, { headers: authHeader() });
      setRegs(data.registrations);
    } catch (e) { toast.error('Could not load registrations'); setRegs([]); }
  };
  useEffect(() => { load(); }, []);

  const setArchived = async (r, archived) => {
    try {
      await axios.post(`${API}/registrations/${r.id}/${archived ? 'archive' : 'unarchive'}`, {}, { headers: authHeader() });
      setRegs(list => list.map(x => x.id === r.id ? { ...x, archived } : x));
      toast.success(archived ? 'Archived — مؤرشف' : 'Restored — تمت الاستعادة');
    } catch { toast.error('Action failed'); }
  };

  if (regs === null) return <p className="text-gray-500">Loading…</p>;
  const active = regs.filter(r => !r.archived);
  const archived = regs.filter(r => r.archived);

  const Card = ({ r, i, isArchived }) => (
    <div className={`glass-card rounded-sm p-5 ${isArchived ? 'opacity-60' : ''}`} data-testid={`admin-registration-${isArchived ? 'archived-' : ''}${i}`}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-display text-xl text-white tracking-wide">{r.playerName}</span>
        <span className="text-xs uppercase tracking-widest text-brand-gold">{r.formType}</span>
        <span className="text-xs text-gray-500">{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</span>
        <button onClick={() => setArchived(r, !isArchived)} data-testid={`archive-button-${isArchived ? 'restore-' : ''}${i}`}
          className={`ml-auto flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors duration-300 ${
            isArchived ? 'border-brand-gold/50 text-brand-gold hover:bg-brand-gold hover:text-night' : 'border-white/15 text-gray-400 hover:border-brand-red hover:text-brand-red'
          }`}>
          {isArchived ? <><RotateCcw size={12} /> Restore</> : <><Archive size={12} /> Archive</>}
        </button>
      </div>
      <div className="mt-2 grid sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-gray-400">
        <span>Parent: {r.parentName}</span>
        <span>Phone: {r.phone}</span>
        <span className="break-all">Email: {r.email}</span>
        {r.program && <span>Program: {r.program}</span>}
        {r.age && <span>Age: {r.age}</span>}
        {r.experienceLevel && <span>Level: {r.experienceLevel}</span>}
        {r.waiverSignature && <span className="text-brand-gold">Waiver signed: {r.waiverSignature}</span>}
      </div>
      {r.message && <p className="mt-2 text-sm text-gray-500 italic">“{r.message}”</p>}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-400">{active.length} active · {archived.length} archived</p>
        <button onClick={load} className="flex items-center gap-2 text-sm text-brand-gold hover:brightness-110"><RefreshCw size={14} /> Refresh</button>
      </div>
      {active.length === 0 ? (
        <div className="glass-card rounded-sm p-10 text-center text-gray-500 flex flex-col items-center gap-3"><Inbox size={28} /> No active registrations.</div>
      ) : (
        <div className="space-y-3">
          {active.map((r, i) => <Card key={r.id || i} r={r} i={i} isArchived={false} />)}
        </div>
      )}
      {archived.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <Archive size={16} className="text-gray-500" />
            <span className="text-xs uppercase tracking-[0.25em] text-gray-500 font-semibold">Archived — الأرشيف ({archived.length})</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <div className="space-y-3">
            {archived.map((r, i) => <Card key={r.id || i} r={r} i={i} isArchived={true} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success('Welcome back, Coach');
      onLogin();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Login failed');
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-night flex items-center justify-center px-4" data-testid="admin-login">
      <form onSubmit={submit} className="glass-card gold-hairline rounded-sm p-8 sm:p-10 w-full max-w-md">
        <img src="/assets/asa-logo.jpeg" alt="ASA" className="w-16 h-16 rounded-full object-cover ring-1 ring-brand-gold/50 mx-auto" />
        <h1 className="mt-6 font-display text-3xl text-white text-center uppercase tracking-wide">Admin Login</h1>
        <p className="mt-1 text-center text-sm text-gray-500">لوحة تحكم المدير — Abu Zama Soccer Academy</p>
        <div className="mt-8 space-y-5">
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className={inputCls} value={username} onChange={e => setUsername(e.target.value)} required data-testid="admin-username" autoComplete="username" />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input type="password" className={inputCls} value={password} onChange={e => setPassword(e.target.value)} required data-testid="admin-password" autoComplete="current-password" />
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-button"
            className="w-full flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redhover disabled:opacity-60 text-white font-display tracking-widest text-xl py-3.5 rounded-sm transition-colors duration-300">
            <Lock size={17} /> {loading ? 'SIGNING IN…' : 'SIGN IN — دخول'}
          </button>
          <Link to="/" className="block text-center text-sm text-gray-500 hover:text-brand-gold transition-colors">← Back to website</Link>
        </div>
      </form>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState('checking');
  const [tab, setTab] = useState('CONTACT');

  useEffect(() => { document.title = 'Admin | Abu Zama Soccer Academy'; }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuth('login'); return; }
    axios.get(`${API}/auth/verify`, { headers: authHeader() })
      .then(() => setAuth('ready'))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuth('login'); });
  }, []);

  if (auth === 'checking') {
    return <div className="min-h-screen bg-night flex items-center justify-center"><img src="/assets/asa-logo.jpeg" alt="ASA" className="w-16 h-16 rounded-full animate-pulse" /></div>;
  }
  if (auth === 'login') return <LoginForm onLogin={() => setAuth('ready')} />;

  const active = TABS.find(t => t.key === tab);
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuth('login'); };

  return (
    <div className="min-h-screen bg-night" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/asa-logo.jpeg" alt="ASA" className="w-9 h-9 rounded-full object-cover ring-1 ring-brand-gold/50" />
            <div className="leading-none">
              <div className="font-display text-lg text-white tracking-wide">ASA ADMIN</div>
              <div className="text-[9px] tracking-[0.3em] text-brand-gold font-semibold">لوحة تحكم المدير</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-brand-gold transition-colors px-3 py-2" data-testid="admin-view-site">
              <ExternalLink size={14} /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-brand-red transition-colors px-3 py-2">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <p className="text-sm text-gray-400 mb-6">Edit any section and press Save — the website updates instantly for all visitors. عدّل أي قسم ثم اضغط حفظ.</p>
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1" data-testid="admin-tabs">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key.toLowerCase()}`}
              className={`whitespace-nowrap font-display tracking-widest text-base uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                tab === t.key ? 'bg-brand-gold text-night border-brand-gold' : 'border-white/15 text-gray-400 hover:border-brand-gold/50 hover:text-brand-gold'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 glass-card rounded-sm p-6 sm:p-8" key={tab}>
          <h2 className="font-display text-2xl text-white uppercase tracking-wide mb-6">{active.label}</h2>
          {active.type === 'object' && <ObjectEditor tab={active} />}
          {active.type === 'array' && <ArrayEditor tab={active} />}
          {active.type === 'strings' && <StringsEditor tab={active} />}
          {active.type === 'spots' && <SpotsEditor />}
          {active.type === 'password' && <ChangePassword />}
          {active.type === 'registrations' && <RegistrationsViewer />}
        </div>
      </div>
    </div>
  );
}
